import { useState } from "react";
import { Grid, TextField, FormControlLabel, Checkbox } from "@material-ui/core";
import { LessonCollectableCategoryFormStyles } from "./lesson-collectable-category-form.styles";
import { useForm, SubmitHandler } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { addLessonCollectableCategory } from "../../learning-path/learningPath.service";
import { Button as RoundedButton } from "../../shared/components/button";
import axios, { AxiosError } from "axios";

interface LessonCollectableCategory {
  id: number;
  name: string;
  isPrivate: boolean;
}

const CATEGORY_CHARACTER_LIMIT = 45;
const formValidations = z.object({
  name: z
    .string()
    .min(1, "category name is required")
    .max(CATEGORY_CHARACTER_LIMIT),
});
type LessonCollectableCategorySchema = z.infer<typeof formValidations>;

interface LessonCollectableCategoryProps {
  onClose: () => void;
}

export const LessonCollectableCategoryForm = (
  props: LessonCollectableCategoryProps
) => {
  const { onClose } = props;
  const classes = LessonCollectableCategoryFormStyles();

  const [editableData, setEditableData] = useState<LessonCollectableCategory>({
    id: 0,
    name: "",
    isPrivate: false,
  });
  const [saveCategoryErrorText, setSaveCategoryErrorText] = useState("");

  const queryClient = useQueryClient();

  const { mutate, isLoading: isAdding } = useMutation(
    addLessonCollectableCategory,
    {
      retry: 0,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["participant-lesson-category"],
        });

        onClose();
      },
      onError: (err: Error | AxiosError) => {
        let saveCategoryErrorText = "";

        if (axios.isAxiosError(err)) {
          const { response } = err;

          saveCategoryErrorText = response.data.getErrors;
        } else {
          saveCategoryErrorText =
            "An unknown error happened. Please try again.";
        }

        setSaveCategoryErrorText(saveCategoryErrorText);
      },
    }
  );

  const {
    register,
    handleSubmit,
    formState: { isValid },
  } = useForm<LessonCollectableCategorySchema>({
    resolver: zodResolver(formValidations),
  });

  const handleCheckChange = (e) =>
    setEditableData({ ...editableData, ...{ isPrivate: e.target.checked } });

  const onSubmit: SubmitHandler<LessonCollectableCategorySchema> = (
    formData
  ) => {
    setSaveCategoryErrorText("");

    const { name } = formData;
    const { isPrivate } = editableData;

    const request = {
      id: null,
      name,
      isPrivate,
    };

    mutate(request);
  };

  return (
    <form data-testid="shared-collectable-form">
      <Grid container spacing={1}>
        <Grid item xs={12} />
        <Grid item xs={12} md={12}>
          <TextField
            {...register("name")}
            variant="outlined"
            margin="normal"
            fullWidth
            id="categoryName"
            label="Create Category"
            name="name"
            defaultValue={editableData?.name}
            autoFocus
            inputProps={{
              maxLength: CATEGORY_CHARACTER_LIMIT,
              "data-testid": "highlight-title",
            }}
            helperText={saveCategoryErrorText}
            error={!!saveCategoryErrorText}
          />
        </Grid>
        <Grid item xs={12} md={12}>
          <FormControlLabel
            label="Private"
            className={classes.checkbox}
            control={
              <Checkbox
                checked={editableData?.isPrivate}
                onChange={handleCheckChange}
              />
            }
          />
        </Grid>
        <Grid item xs={12} />
        <Grid item container xs={12} md={12} justifyContent="flex-end">
          <RoundedButton variant="text" onClick={onClose}>
            Cancel
          </RoundedButton>
          <RoundedButton
            variant="contained"
            color="primary"
            disabled={!isValid || isAdding}
            onClick={handleSubmit(onSubmit)}
          >
            Create
          </RoundedButton>
        </Grid>
        <Grid item xs={12}>
          <hr className={classes.separator} />
        </Grid>
      </Grid>
    </form>
  );
};
